import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { CheckCircle2, Building2, Banknote, Copy } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PublicTarifs() {
  const [plans, setPlans] = useState([]);
  const [billing, setBilling] = useState(null);
  const [loading, setLoading] = useState(true);
  const [annuel, setAnnuel] = useState(false);
  const [claimModal, setClaimModal] = useState(null);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/public/plans`).then(r => r.data?.items || []),
      axios.get(`${API}/public/billing-info`).then(r => r.data || {}),
    ]).then(([p, b]) => {
      setPlans(p);
      setBilling(b);
    }).catch(() => toast.error('Erreur chargement des tarifs'))
      .finally(() => setLoading(false));
  }, []);

  const fmtPrix = (n) => Number(n || 0).toLocaleString('fr-FR');
  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copié`);
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center text-stone-500">Chargement...</div>;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-stone-50 via-white to-sky-50">
      {/* Header */}
      <header className="border-b border-stone-200 bg-white/80 backdrop-blur-md sticky top-0 z-20">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-sky-500 to-orange-500 flex items-center justify-center text-white font-black">T</div>
            <div className="leading-tight">
              <div className="font-black text-slate-900">TripNova</div>
              <div className="text-[10px] uppercase tracking-wide text-slate-500">CRM Agences de Voyage</div>
            </div>
          </div>
          <a href="/login" className="text-sm font-semibold text-sky-600 hover:text-sky-700">Connexion →</a>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-12 sm:py-16">
        {/* Hero */}
        <section className="text-center mb-12">
          <h1 className="text-4xl sm:text-5xl font-black text-slate-900 tracking-tight">Tarifs simples et transparents</h1>
          <p className="mt-4 text-lg text-slate-600 max-w-2xl mx-auto">
            Choisissez votre plan, virez le montant, et votre agence est activée sous 24h ouvrées.
          </p>

          {/* Switch mensuel/annuel */}
          <div className="inline-flex items-center gap-1 mt-8 p-1 bg-white border border-stone-200 rounded-full">
            <button
              onClick={() => setAnnuel(false)}
              className={`px-4 py-1.5 text-sm font-semibold rounded-full transition ${!annuel ? 'bg-slate-900 text-white' : 'text-slate-600'}`}
              data-testid="tarifs-toggle-mensuel"
            >Mensuel</button>
            <button
              onClick={() => setAnnuel(true)}
              className={`px-4 py-1.5 text-sm font-semibold rounded-full transition ${annuel ? 'bg-slate-900 text-white' : 'text-slate-600'}`}
              data-testid="tarifs-toggle-annuel"
            >Annuel <span className="ml-1 text-[10px] text-emerald-500">-16%</span></button>
          </div>
        </section>

        {/* Plans grid */}
        <section className="grid md:grid-cols-3 gap-6 mb-16">
          {plans.length === 0 && (
            <p className="md:col-span-3 text-center text-slate-500">Aucun plan actif pour le moment.</p>
          )}
          {plans.map((p) => {
            const dureeBase = p.duree_mois || 1;
            const prixMensuel = p.prix_dzd / dureeBase;
            const prixAffiche = annuel ? prixMensuel * 10 : prixMensuel; // -2 mois si annuel
            const totalAnnuel = annuel ? prixMensuel * 10 : prixMensuel * 12;
            const economie = annuel ? prixMensuel * 2 : 0;
            return (
              <div
                key={p.id}
                className={`relative bg-white rounded-3xl border-2 p-6 sm:p-8 transition hover:shadow-xl ${
                  p.is_popular ? 'border-orange-400 shadow-lg shadow-orange-100' : 'border-stone-200'
                }`}
                data-testid={`tarifs-plan-${p.code}`}
              >
                {p.is_popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-orange-500 text-white text-[10px] font-bold uppercase tracking-wider rounded-full">
                    ★ Populaire
                  </div>
                )}
                <h3 className="text-2xl font-black text-slate-900">{p.nom}</h3>
                <p className="mt-1 text-sm text-slate-500">{p.code === 'starter' ? 'Pour démarrer en solo' : p.code === 'pro' ? 'Pour les équipes en croissance' : 'Pour les agences premium'}</p>
                <div className="mt-6 flex items-baseline gap-1">
                  <span className="text-4xl font-black text-slate-900">{fmtPrix(Math.round(prixAffiche))}</span>
                  <span className="text-slate-500">DZD / mois</span>
                </div>
                {annuel && economie > 0 && (
                  <p className="text-xs text-emerald-600 font-semibold mt-1">
                    Économie {fmtPrix(Math.round(economie * 12 / 12))} DZD / an
                  </p>
                )}
                <p className="text-[11px] text-slate-400 mt-1">
                  {annuel ? `Facturé ${fmtPrix(Math.round(totalAnnuel))} DZD / an` : 'Sans engagement'}
                </p>
                <ul className="mt-6 space-y-2.5 min-h-[180px]">
                  {(p.features || []).map((f, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                      <CheckCircle2 size={16} className="shrink-0 mt-0.5 text-emerald-500" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <button
                  onClick={() => setClaimModal({ plan: p, montant: Math.round(prixAffiche * (annuel ? 10 : 1)), duree: annuel ? 10 : 1 })}
                  className={`mt-8 w-full py-3 rounded-xl font-bold text-sm transition ${
                    p.is_popular
                      ? 'bg-orange-500 text-white hover:bg-orange-600'
                      : 'bg-slate-900 text-white hover:bg-slate-800'
                  }`}
                  data-testid={`tarifs-choose-${p.code}`}
                >
                  Choisir {p.nom}
                </button>
              </div>
            );
          })}
        </section>

        {/* Section RIB */}
        <section className="bg-gradient-to-br from-slate-900 to-slate-800 text-white rounded-3xl p-8 sm:p-12">
          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <Banknote className="w-12 h-12 text-orange-400 mb-4" />
              <h2 className="text-3xl font-black mb-3">Paiement par virement bancaire</h2>
              <p className="text-slate-300 leading-relaxed">
                Stripe / cartes bancaires internationales ne sont pas disponibles en Algérie. Nous acceptons donc les
                <strong className="text-white"> virements bancaires</strong> uniquement, sous 24h ouvrées d'activation après réception.
              </p>
              <p className="text-sm text-slate-400 mt-4">
                Après votre virement, cliquez sur <strong>« Choisir un plan »</strong> ci-dessus puis « J'ai effectué mon virement » pour nous notifier avec votre référence.
              </p>
              {billing?.support_email && (
                <p className="text-sm text-slate-400 mt-2">
                  Support : <a href={`mailto:${billing.support_email}`} className="text-sky-300 underline">{billing.support_email}</a>
                  {billing.support_phone && <> · {billing.support_phone}</>}
                </p>
              )}
            </div>
            <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-6 space-y-3 text-sm" data-testid="tarifs-rib-section">
              {!billing?.configured ? (
                <p className="text-orange-300 italic">
                  RIB en cours de configuration. Contactez-nous par email pour recevoir nos coordonnées bancaires.
                </p>
              ) : (
                <>
                  <RibRow label="Banque" value={billing.bank_name} />
                  <RibRow label="Agence" value={billing.agency_name} />
                  <RibRow label="Bénéficiaire" value={billing.beneficiary} />
                  <RibRow label="RIB / N° de compte" value={billing.rib} copy onCopy={(v) => copyToClipboard(v, 'RIB')} />
                  {billing.iban && <RibRow label="IBAN" value={billing.iban} copy onCopy={(v) => copyToClipboard(v, 'IBAN')} />}
                  {billing.swift && <RibRow label="SWIFT / BIC" value={billing.swift} />}
                  {billing.instructions && (
                    <div className="pt-3 border-t border-white/10">
                      <div className="text-[10px] uppercase tracking-wider text-slate-400 mb-1">Instructions</div>
                      <p className="text-slate-200 text-xs leading-relaxed whitespace-pre-wrap">{billing.instructions}</p>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </section>
      </main>

      <footer className="border-t border-stone-200 mt-12 py-8 text-center text-xs text-slate-500">
        <div className="space-x-4">
          <a href="/legal/cgu" className="hover:text-slate-900">CGU</a>
          <a href="/legal/cgv" className="hover:text-slate-900">CGV</a>
          <a href="/legal/rgpd" className="hover:text-slate-900">Confidentialité</a>
          <a href="/legal/mentions-legales" className="hover:text-slate-900">Mentions légales</a>
        </div>
        <div className="mt-3">© 2026 TripNova · Fait avec ❤ en Algérie</div>
      </footer>

      {claimModal && (
        <VirementClaimModal
          plan={claimModal.plan}
          montant={claimModal.montant}
          duree={claimModal.duree}
          billing={billing}
          onClose={() => setClaimModal(null)}
        />
      )}
    </div>
  );
}

function RibRow({ label, value, copy, onCopy }) {
  if (!value) return null;
  return (
    <div className="flex items-center justify-between gap-3">
      <div className="flex-1 min-w-0">
        <div className="text-[10px] uppercase tracking-wider text-slate-400">{label}</div>
        <div className="text-white font-mono break-all">{value}</div>
      </div>
      {copy && (
        <button
          type="button"
          onClick={() => onCopy(value)}
          className="shrink-0 p-2 hover:bg-white/10 rounded-lg transition"
          data-testid={`copy-${label.toLowerCase().replace(/[^a-z]/g, '')}`}
        >
          <Copy size={14} className="text-slate-300" />
        </button>
      )}
    </div>
  );
}

function VirementClaimModal({ plan, montant, duree, billing, onClose }) {
  const [form, setForm] = useState({
    agence_nom: '',
    telephone: '',
    email: '',
    reference: '',
    date_virement: new Date().toISOString().slice(0, 10),
    message: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.agence_nom.trim() || !form.telephone.trim() || !form.email.includes('@') || !form.reference.trim()) {
      toast.error('Veuillez remplir tous les champs obligatoires (*)');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/public/virement-claim`, {
        ...form,
        plan_code: plan.code,
        montant_dzd: montant,
        duree_mois: duree,
      });
      setDone(true);
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Erreur lors de l\'envoi';
      toast.error(typeof msg === 'string' ? msg : 'Erreur');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-white rounded-3xl max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="virement-claim-modal">
        {done ? (
          <div className="p-8 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-emerald-100 text-emerald-600 mb-4">
              <CheckCircle2 size={32} />
            </div>
            <h3 className="text-2xl font-black text-slate-900">Déclaration enregistrée !</h3>
            <p className="text-slate-600 mt-3">
              Notre équipe va vérifier votre virement (Banque : <strong>{billing?.bank_name || 'votre banque'}</strong>)
              sous <strong>24h ouvrées</strong> et activer votre abonnement <strong>{plan.nom}</strong>.
            </p>
            <p className="text-sm text-slate-500 mt-4">
              Vous recevrez un email de confirmation à <strong>{form.email}</strong>.
            </p>
            <button onClick={onClose} className="mt-6 w-full py-3 bg-slate-900 text-white rounded-xl font-bold" data-testid="claim-done-close">Fermer</button>
          </div>
        ) : (
          <form onSubmit={submit} className="p-6 sm:p-8 space-y-4">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h3 className="text-2xl font-black text-slate-900">J'ai effectué mon virement</h3>
                <p className="text-sm text-slate-600 mt-1">
                  Plan <strong>{plan.nom}</strong> · <strong>{Number(montant).toLocaleString('fr-FR')} DZD</strong> · {duree} mois
                </p>
              </div>
              <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-700 text-xl leading-none">×</button>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-800 flex gap-2">
              <Building2 size={16} className="shrink-0 mt-0.5" />
              <span>Effectuez d'abord votre virement sur le RIB ci-dessus, puis remplissez ce formulaire avec la référence du virement.</span>
            </div>

            <Field label="Nom de l'agence *" testid="claim-agence">
              <input
                type="text"
                value={form.agence_nom}
                onChange={(e) => setForm({ ...form, agence_nom: e.target.value })}
                className="w-full px-3 py-2 border border-stone-300 rounded-lg"
                required
                data-testid="claim-agence-input"
              />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Téléphone *" testid="claim-tel">
                <input
                  type="tel"
                  value={form.telephone}
                  onChange={(e) => setForm({ ...form, telephone: e.target.value })}
                  placeholder="+213 555 00 00 00"
                  className="w-full px-3 py-2 border border-stone-300 rounded-lg"
                  required
                  data-testid="claim-tel-input"
                />
              </Field>
              <Field label="Email *" testid="claim-email">
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="w-full px-3 py-2 border border-stone-300 rounded-lg"
                  required
                  data-testid="claim-email-input"
                />
              </Field>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Référence du virement *" testid="claim-ref">
                <input
                  type="text"
                  value={form.reference}
                  onChange={(e) => setForm({ ...form, reference: e.target.value })}
                  placeholder="Ex: TRF-2026-001234"
                  className="w-full px-3 py-2 border border-stone-300 rounded-lg"
                  required
                  data-testid="claim-ref-input"
                />
              </Field>
              <Field label="Date du virement" testid="claim-date">
                <input
                  type="date"
                  value={form.date_virement}
                  onChange={(e) => setForm({ ...form, date_virement: e.target.value })}
                  className="w-full px-3 py-2 border border-stone-300 rounded-lg"
                  data-testid="claim-date-input"
                />
              </Field>
            </div>
            <Field label="Message (optionnel)" testid="claim-msg">
              <textarea
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                rows={2}
                className="w-full px-3 py-2 border border-stone-300 rounded-lg resize-none"
                placeholder="Précisions, demandes particulières..."
                data-testid="claim-msg-input"
              />
            </Field>

            <button
              type="submit"
              disabled={submitting}
              className="w-full py-3 bg-slate-900 text-white rounded-xl font-bold hover:bg-slate-800 disabled:opacity-50"
              data-testid="claim-submit"
            >
              {submitting ? 'Envoi...' : 'Notifier notre équipe'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

function Field({ label, children, testid }) {
  return (
    <div data-testid={testid}>
      <label className="block text-[11px] font-semibold uppercase tracking-wider text-slate-600 mb-1">{label}</label>
      {children}
    </div>
  );
}
